// Report and offboarding rules, reproducing the prototype's setReportRange,
// renderStaffActivityReports, renderMissedReport, renderPerformanceReports, offboardWork.
// Inputs are plain rows; dates compare as agency-local calendar days (YYYY-MM-DD).
import { AGENCY_TIME_ZONE, todayIn } from './index.js';
export const REPORT_PRESETS = ['week', 'month', 'quarter', 'year', 'thisMonth', 'thisQuarter', 'thisYear', 'previousYear'];
export const PRESET_LABELS = {
    week: 'Last 7 days', month: 'Last 30 days', quarter: 'Last 90 days', year: 'Last 365 days',
    thisMonth: 'This month', thisQuarter: 'This quarter', thisYear: 'This year', previousYear: 'Previous year',
};
const shift = (iso, days) => { const d = new Date(`${iso}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + days); return d.toISOString().slice(0, 10); };
/** Prototype setReportRange: rolling windows include today; calendar windows start on the 1st. */
export function rangeFor(preset, today) {
    const [y, m] = today.split('-').map(Number);
    switch (preset) {
        case 'week': return { from: shift(today, -6), to: today };
        case 'month': return { from: shift(today, -29), to: today };
        case 'quarter': return { from: shift(today, -89), to: today };
        case 'year': return { from: shift(today, -364), to: today };
        case 'thisMonth': return { from: `${y}-${String(m).padStart(2, '0')}-01`, to: today };
        case 'thisQuarter': return { from: `${y}-${String(Math.floor((m - 1) / 3) * 3 + 1).padStart(2, '0')}-01`, to: today };
        case 'thisYear': return { from: `${y}-01-01`, to: today };
        case 'previousYear': return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    }
}
/**
 * Agency-local calendar day. Plain dates (YYYY-MM-DD) are already local; timestamps are UTC
 * and must be converted, or evening activity in Iowa lands on the next day.
 */
export const dayOf = (isoOrDate, timeZone = AGENCY_TIME_ZONE) => {
    if (!isoOrDate)
        return '';
    const s = String(isoOrDate);
    if (s.length === 10)
        return s;
    const t = Date.parse(s);
    return Number.isNaN(t) ? s.slice(0, 10) : todayIn(timeZone, new Date(t));
};
export const inRange = (isoOrDate, r) => { const d = dayOf(isoOrDate); return Boolean(d) && r.from <= r.to && d >= r.from && d <= r.to; };
const countBy = (items, key) => {
    const m = new Map();
    for (const x of items)
        m.set(key(x), (m.get(key(x)) ?? 0) + 1);
    return [...m].map(([membershipId, count]) => ({ membershipId, count })).sort((a, b) => b.count - a.count);
};
export function activityReport(calls, quotes, assignments, r) {
    const callsInRange = calls.filter((c) => inRange(c.callDate, r));
    // "Assigned during period": unique opportunities with any assignment overlapping the range.
    const during = new Map();
    for (const a of assignments) {
        if (dayOf(a.startedAt) <= r.to && (!a.endedAt || dayOf(a.endedAt) >= r.from)) {
            if (!during.has(a.membershipId))
                during.set(a.membershipId, new Set());
            during.get(a.membershipId).add(a.opportunityId);
        }
    }
    return {
        callsLogged: countBy(callsInRange, (c) => c.messageByMembershipId),
        outgoingCalls: countBy(callsInRange.filter((c) => c.status === 'Outgoing'), (c) => c.messageByMembershipId),
        currentQuoting: countBy(quotes.filter((q) => q.pcStatus === 'Quoting'), (q) => q.ownerMembershipId),
        assignedDuringPeriod: [...during].map(([membershipId, ids]) => ({ membershipId, count: ids.size })).sort((a, b) => b.count - a.count),
    };
}
/** Each Missed event with its outcome: Reopened / Recovered if those followed before the next miss. */
export function missedEvents(events) {
    const sorted = [...events].sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));
    const out = [];
    sorted.forEach((e, i) => {
        if (e.type !== 'Missed')
            return;
        let outcome = 'Missed';
        for (const later of sorted.slice(i + 1)) {
            if (later.opportunityId !== e.opportunityId || later.coverage !== e.coverage)
                continue;
            if (later.type === 'Missed')
                break;
            if (later.type === 'Reopened' && outcome === 'Missed')
                outcome = 'Reopened';
            if (later.type === 'Recovered') {
                outcome = 'Recovered';
                break;
            }
        }
        out.push({ ...e, outcome });
    });
    return out;
}
export function missedReport(events, r, filter = {}) {
    const misses = missedEvents(events)
        .filter((e) => inRange(e.occurredAt, r))
        .filter((e) => (!filter.membershipId || e.ownerMembershipId === filter.membershipId) && (!filter.coverage || e.coverage === filter.coverage));
    const byStaff = new Map();
    for (const e of misses) {
        const row = byStaff.get(e.ownerMembershipId) ?? { pc: 0, life: 0 };
        if (e.coverage === 'PC')
            row.pc++;
        else
            row.life++;
        byStaff.set(e.ownerMembershipId, row);
    }
    return {
        pcMissed: misses.filter((e) => e.coverage === 'PC').length,
        lifeMissed: misses.filter((e) => e.coverage === 'Life').length,
        reopened: misses.filter((e) => e.outcome !== 'Missed').length,
        recovered: misses.filter((e) => e.outcome === 'Recovered').length,
        stillMissed: misses.filter((e) => e.outcome === 'Missed').length,
        byStaff: [...byStaff].map(([membershipId, v]) => ({ membershipId, ...v, total: v.pc + v.life })).sort((a, b) => b.total - a.total),
        details: misses.sort((a, b) => b.occurredAt.localeCompare(a.occurredAt)),
    };
}
// ---------- sales performance (management) ----------
export function salesReport(events, quotes, r) {
    const created = new Map(quotes.map((q) => [q.opportunityId, q.createdAt]));
    const done = events.filter((e) => e.type === 'Completed' && inRange(e.occurredAt, r));
    const days = (e) => { const c = created.get(e.opportunityId); return c ? Math.max(0, Math.round((Date.parse(e.occurredAt) - Date.parse(c)) / 86_400_000)) : null; };
    const staff = new Map();
    const months = new Map();
    for (const e of done) {
        const s = staff.get(e.ownerMembershipId) ?? { pc: 0, life: 0, cycle: [] };
        if (e.coverage === 'PC')
            s.pc++;
        else
            s.life++;
        const d = days(e);
        if (d !== null)
            s.cycle.push(d);
        staff.set(e.ownerMembershipId, s);
        const key = dayOf(e.occurredAt).slice(0, 7);
        const mo = months.get(key) ?? { pc: 0, life: 0 };
        if (e.coverage === 'PC')
            mo.pc++;
        else
            mo.life++;
        months.set(key, mo);
    }
    const avg = (xs) => (xs.length ? Math.round((xs.reduce((a, b) => a + b, 0) / xs.length) * 10) / 10 : null);
    return {
        pcCompleted: done.filter((e) => e.coverage === 'PC').length,
        lifeCompleted: done.filter((e) => e.coverage === 'Life').length,
        total: done.length,
        customers: new Set(done.map((e) => e.opportunityId)).size,
        averageDaysToComplete: avg(done.map(days).filter((d) => d !== null)),
        byStaff: [...staff].map(([membershipId, s]) => ({ membershipId, pc: s.pc, life: s.life, total: s.pc + s.life, averageDays: avg(s.cycle) })).sort((a, b) => b.total - a.total),
        byMonth: [...months].sort(([a], [b]) => a.localeCompare(b)).map(([month, v]) => ({ month, ...v, total: v.pc + v.life })),
        details: done.sort((a, b) => b.occurredAt.localeCompare(a.occurredAt)),
    };
}
// ---------- offboarding (prototype offboardWork) ----------
/** Open work moves to the replacement: anything not completed on every coverage it uses. */
export const isOpenQuote = (q) => !(q.pcStatus === 'Completed' && (!q.lifeStatus || q.lifeStatus === 'Completed'));
export const isOpenCall = (c) => c.status !== 'Completed';
//# sourceMappingURL=reports.js.map